import { createRouter, createWebHistory } from 'vue-router';
import appRoutes from './routes';
import { trackRouter } from 'vue-gtag-next';
import { rg4js } from "./lib/raygun";
import { useOrganizationStore } from './store/organization';
import { waitForAuthReady } from './lib/auth/authReady';
import { useUserStore } from './store/user';
import { user } from './lib/nucleus';

let previousRoute = null;

const routes = [
    ...appRoutes,
    {
        path: '/',
        name: 'dashboard',
        component: () => import('./pages/Dashboard.vue'),
        meta: {
            layout: 'default',
        },
    },
    {
        path: '/welcome',
        name: 'landing',
        component: () => import('./pages/Landing.vue'),
        meta: {
            layout: 'default',
        },
    },
    {
        path: '/organization-error',
        name: 'organization-error',
        meta: { layout: 'auth', global: true, public: true },
        component: () => import('./pages/auth/OrganizationError.vue'),
    },
    {
        path: '/organization-new',
        name: 'organization-new',
        meta: { layout: 'empty', public: true },
        component: () => import('./pages/organization/New.vue'),
    },
    {
        path: '/unauthorized',
        name: 'unauthorized',
        meta: { layout: 'default', public: true },
        component: () => import('./pages/Unauthorized.vue'),
    },
    {
        path: '/:catchAll(.*)',
        name: 'notfound',
        meta: { layout: 'empty' },
        component: () => import('./pages/NotFound.vue'),
    },
];

const router = createRouter({
    history: createWebHistory(),
    routes,
});

router.beforeEach(async (to, from, next) => {
    // 1. Skip the guard (including org-query injection below) for public
    // routes. Public routes are pre-auth/org-agnostic; some, like
    // auth/ott-callback, carry state in the URL fragment that a next({path,
    // query}) redirect would silently drop (the fragment isn't part of the
    // target location, so Vue Router's History API navigation loses it).
    if (to.meta.public === true) return next();

    const currentOrg = useOrganizationStore().orgId || to.query.org;

    if (!to.query.org && currentOrg) {
      return next({
        path: to.path,
        query: { ...to.query, org: currentOrg },
      });
    }

    // 2. Ensure auth is ready (Firebase)
    await waitForAuthReady;
  
    const userStore = useUserStore();
    const currentUser = userStore.currentUser;
  
    if (!currentUser?.getIdToken) {
      return next({
        name: 'auth/login',
        query: { redirect: to.fullPath },
      });
    }

    if (to.meta.scaleAdminOnly && !userStore.profile?.scaleAdminRole) {
      return next({ name: 'unauthorized' });
    }

    // 3. Ensure org is present in the URL
    const requestedOrg = to.query.org;

    if (!requestedOrg) {
      try {
        // assume the last org
        let lastOrgId =  useOrganizationStore().orgId;

        // if there is no last org, then assume the first org in the users list.
        if(!lastOrgId) {
          const orgs = await user.getOrganizations();
          lastOrgId = orgs?.data?.[0]?._id;
        }

        //
        if (!lastOrgId) {
          return next({ name: 'auth/invite-only' });
        }
  
        return next({
          path: to.path,
          query: { ...to.query, org: lastOrgId },
          replace: true,
          // By default, `next()` pushes a new entry. Removing `replace: true` ensures the previous route is kept in history.
        });
      } catch (err) {
        rg4js('send', { error: err });
        return next({ name: 'auth/invite-only' });
      }
    }

    // 4. Validate the org from the URL and keep the store on it. The startup
    // batch already fetched the org the page loads on and handed it to the
    // store, so that first lookup is skipped; every navigation after that
    // checks the org with the API, as before.
    const orgStore = useOrganizationStore();
    if (!orgStore.takeValidatedOrganization(requestedOrg)) {
      try {
        await orgStore.setOrgIdAndFetch(requestedOrg, { force: true });
        if (!orgStore.orgData?._id) {
          throw new Error('Invalid organization');
        }
      } catch (err) {
        rg4js('send', { error: err });
        return next({ name: 'organization-error', query: { org: requestedOrg } });
      }
    }
  
    return next();
  });

// google analytics tracking.
trackRouter(router);

export default router;
export { previousRoute };